import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import {
  ALL_CONFIGURABLE_RECEIVING_FIELDS,
  ALL_SAMPLE_FIELD_TYPES,
  suggestStableKey,
} from '@lims/shared-types';
import {
  sampleTypesApi,
  sampleTypeSettingsApi,
  type SampleFieldTypeName,
  type SampleTypeDefinition,
  type SampleTypeField,
  type SampleTypeFieldInput,
} from '../../api/endpoints.js';
import { ApiError } from '../../api/client.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  CheckboxGroupField,
  ConfirmDialog,
  Dialog,
  LoadingState,
  NumberField,
  PageHeader,
  Panel,
  SelectField,
  StatusBadge,
  SwitchField,
  TextField,
  useToast,
} from '../../components/ui/index.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import { formatNumber } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import {
  AdditionalFieldsInputs,
  SAMPLE_TYPES_QUERY_KEY,
  activeFields,
  fieldTypeLabel,
  pickLabel,
} from './sample-type-fields.js';

/**
 * Sample types — the SAMPLE TYPE BUILDER (0024, decisions D296-D305).
 *
 * A laboratory defines the materials it receives and, for each, the ADDITIONAL
 * intake fields it wants to know at the counter: a supplier, a batch number, a
 * casting day. A new type or field appears on the receiving form at once, with
 * no release.
 *
 * WHAT THIS IS NOT. It defines nothing about a test: no reading, mass, sieve,
 * load, strength, result, formula, condition or pass/fail (CLAUDE.md section
 * 4). Those belong to specialised test templates, which never read these fields.
 * The core facts every sample already has are shown as a notice, never as
 * editable rows.
 *
 * HISTORY IS NEVER DESTROYED BY CONFIGURATION (D302). A type, field or option
 * that anything used is archived, never deleted; the server refuses otherwise,
 * and this page offers Delete only where nothing ever used it. The seven
 * baseline types are never deleted at all, used or not (D307), and a type the
 * catalogue classifies tests by counts as used (D306).
 *
 * Changing the configuration needs `settings.update`; everybody who receives
 * samples reads it (D305). The receiving rules — which standard fields a type
 * requires, and its retention — keep their own permissions (D70, D71).
 */

type Locale = ReturnType<typeof currentLocale>;

/** The core facts of every sample, as the notice names them (D296). */
const STANDARD_FIELDS = [
  'receiving.sampleDescription',
  'samples.quantity',
  'samples.quantityUnit',
  'receivingField.client_reference',
  'receiving.sampleNotes',
  'receivingField.source_description',
  'receivingField.sampled_at',
  'receivingField.sampled_by',
  'receivingField.condition_on_receipt',
  'receivingField.storage_location',
] as const;

/** A server refusal's details, by field path, in words. */
function refusalFields(error: unknown, t: (key: string, options?: Record<string, unknown>) => string): Record<string, string> {
  if (!(error instanceof ApiError)) return {};
  return Object.fromEntries(
    error.details
      .filter((detail) => detail.field)
      .map((detail) => [
        detail.field as string,
        t(`sampleTypes.configError.${detail.code ?? ''}`, { defaultValue: t('sampleTypes.configError.invalid') }),
      ]),
  );
}

const typeLabel = (type: SampleTypeDefinition, locale: Locale) =>
  pickLabel(type.nameAr, type.nameEn, locale) || type.code;
const fieldLabel = (field: { labelAr: string | null; labelEn: string | null; key: string }, locale: Locale) =>
  pickLabel(field.labelAr, field.labelEn, locale) || field.key;

/** Delete is offered only for a user-created type nothing has ever used. */
function deletable(type: SampleTypeDefinition): boolean {
  return !type.isSystem && type.usageCount === 0 && type.catalogueUsageCount === 0;
}

export function SampleTypeSettingsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const locale = currentLocale();
  const mayConfigure = can(PERMISSIONS.SETTINGS_UPDATE);

  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [rulesFor, setRulesFor] = useState<SampleTypeDefinition | null>(null);
  const [confirm, setConfirm] = useState<{ type: SampleTypeDefinition; action: 'archive' | 'delete' } | null>(null);

  const types = useQuery({ queryKey: SAMPLE_TYPES_QUERY_KEY, queryFn: () => sampleTypesApi.list() });
  const settings = useQuery({ queryKey: ['sample-type-settings'], queryFn: () => sampleTypeSettingsApi.list() });
  const refresh = () => queryClient.invalidateQueries({ queryKey: SAMPLE_TYPES_QUERY_KEY });

  const lifecycle = useMutation({
    mutationFn: async (input: { type: SampleTypeDefinition; action: 'archive' | 'restore' | 'delete' }) => {
      if (input.action === 'archive') await sampleTypesApi.archive(input.type.id);
      else if (input.action === 'restore') await sampleTypesApi.restore(input.type.id);
      else await sampleTypesApi.remove(input.type.id);
      return input;
    },
    onSuccess: async (input) => {
      await refresh();
      setConfirm(null);
      show({ tone: 'success', message: t(`sampleTypes.done_${input.action}`, { name: typeLabel(input.type, locale) }) });
    },
  });

  const list = [...(types.data ?? [])].sort((a, b) => Number(a.archived) - Number(b.archived));
  const editing = list.find((type) => type.id === editingId) ?? null;

  return (
    <>
      <PageHeader
        title={t('sampleTypeSettings.title')}
        subtitle={t('sampleTypes.subtitle')}
        breadcrumbs={[{ label: t('nav.settings'), to: '/settings' }, { label: t('sampleTypeSettings.title') }]}
        actions={
          mayConfigure ? (
            <ActionButton priority="primary" onPress={() => setCreating(true)}>
              {t('sampleTypes.add')}
            </ActionButton>
          ) : null
        }
      />

      <div className="mb-gutter">
        <Alert tone="info" title={t('sampleTypes.scopeTitle')} description={t('sampleTypes.scopeBody')} />
      </div>

      {types.isLoading ? <LoadingState label={t('common.loading')} /> : null}
      {types.error ? <ErrorNotice error={types.error} /> : null}

      <ul className="flex flex-col gap-stack" aria-label={t('sampleTypeSettings.title')}>
        {list.map((type) => {
          const rule = settings.data?.find((entry) => entry.sampleType === type.code);
          const fields = activeFields(type);
          const other = locale === 'ar' ? type.nameEn : type.nameAr;
          return (
            <li key={type.id} data-testid="sample-type-row" data-code={type.code}>
              <Panel>
                <div className="flex min-w-0 flex-wrap items-start justify-between gap-x-gutter gap-y-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-section font-semibold text-default-900">{typeLabel(type, locale)}</h2>
                      {other && other !== typeLabel(type, locale) ? (
                        <span className="text-body text-default-600">{other}</span>
                      ) : null}
                      <StatusBadge
                        label={type.archived ? t('sampleTypes.archived') : t('sampleTypes.active')}
                        tone={type.archived ? 'neutral' : 'success'}
                      />
                      {type.isSystem ? (
                        <span data-testid="sample-type-system" className="text-caption text-default-600">
                          {t('sampleTypes.system')}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1 text-caption text-default-600">
                      <span dir="ltr" className="font-mono">{type.code}</span>
                      {' · '}
                      {t('sampleTypes.fieldCount', { count: fields.length })}
                      {' · '}
                      {t('sampleTypes.usage', { count: type.usageCount })}
                      {type.catalogueUsageCount > 0 ? (
                        <>
                          {' · '}
                          {t('sampleTypes.catalogueUsage', { count: type.catalogueUsageCount })}
                        </>
                      ) : null}
                    </p>
                    {fields.length > 0 ? (
                      <ul className="mt-2 flex flex-wrap gap-1.5" aria-label={t('sampleTypes.additionalFields')}>
                        {fields.map((field) => (
                          <li
                            key={field.id}
                            className="rounded-small border border-default-300 px-2 py-0.5 text-caption text-default-800"
                          >
                            {fieldLabel(field, locale)}
                            {field.isRequired ? <span className="text-danger-700"> *</span> : null}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    <p className="mt-2 text-caption text-default-600">
                      {t('sampleTypes.rulesSummary', {
                        required:
                          rule && rule.requiredFields.length > 0
                            ? rule.requiredFields.map((field) => t(`receivingField.${field}`)).join(t('samples.fieldSeparator'))
                            : t('sampleTypes.rulesNone'),
                        retention:
                          rule?.retentionDays === null || rule?.retentionDays === undefined
                            ? t('sampleTypeSettings.keptIndefinitely')
                            : t('sampleTypeSettings.retentionDaysValue', {
                                days: formatNumber(rule.retentionDays, { locale }) ?? '',
                              }),
                      })}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    {mayConfigure ? (
                      <ActionButton
                        priority="secondary"
                        size="sm"
                        aria-label={t('sampleTypes.editLabel', { name: typeLabel(type, locale) })}
                        onPress={() => setEditingId(type.id)}
                      >
                        {t('common.edit')}
                      </ActionButton>
                    ) : null}
                    {can(PERMISSIONS.SAMPLES_UPDATE) || can(PERMISSIONS.SAMPLE_STORAGE_MANAGE) ? (
                      <ActionButton priority="tertiary" size="sm" onPress={() => setRulesFor(type)}>
                        {t('sampleTypes.receivingRules')}
                      </ActionButton>
                    ) : null}
                    {mayConfigure ? (
                      type.archived ? (
                        <ActionButton
                          priority="tertiary"
                          size="sm"
                          isLoading={lifecycle.isPending && lifecycle.variables?.type.id === type.id}
                          onPress={() => lifecycle.mutate({ type, action: 'restore' })}
                        >
                          {t('sampleTypes.restore')}
                        </ActionButton>
                      ) : (
                        <ActionButton priority="tertiary" size="sm" onPress={() => setConfirm({ type, action: 'archive' })}>
                          {t('sampleTypes.archive')}
                        </ActionButton>
                      )
                    ) : null}
                    {mayConfigure && deletable(type) ? (
                      <ActionButton priority="destructive" size="sm" onPress={() => setConfirm({ type, action: 'delete' })}>
                        {t('common.remove')}
                      </ActionButton>
                    ) : null}
                  </div>
                </div>
              </Panel>
            </li>
          );
        })}
      </ul>

      {creating ? (
        <SampleTypeEditor
          type={null}
          onClose={() => setCreating(false)}
          onCreated={async (created) => {
            await refresh();
            setCreating(false);
            setEditingId(created.id);
          }}
        />
      ) : null}
      {editing ? <SampleTypeEditor type={editing} onClose={() => setEditingId(null)} /> : null}
      {rulesFor ? <ReceivingRulesDialog type={rulesFor} onClose={() => setRulesFor(null)} /> : null}

      <ConfirmDialog
        isOpen={confirm !== null}
        onClose={() => {
          setConfirm(null);
          lifecycle.reset();
        }}
        title={
          confirm
            ? t(confirm.action === 'delete' ? 'sampleTypes.deleteTitle' : 'sampleTypes.archiveTitle', {
                name: typeLabel(confirm.type, locale),
              })
            : ''
        }
        message={
          confirm
            ? confirm.action === 'delete'
              ? t('sampleTypes.deleteBody')
              : t('sampleTypes.archiveBody', { count: confirm.type.usageCount })
            : ''
        }
        confirmLabel={confirm?.action === 'delete' ? t('common.remove') : t('sampleTypes.archive')}
        confirmPriority={confirm?.action === 'delete' ? 'destructive' : 'primary'}
        isPending={lifecycle.isPending}
        onConfirm={() => {
          if (confirm) lifecycle.mutate(confirm);
        }}
      >
        {lifecycle.error ? <ErrorNotice error={lifecycle.error} /> : null}
      </ConfirmDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Create / edit a sample type
// ---------------------------------------------------------------------------

/** A field as the editor holds it: saved (with an id) or, while creating, a draft. */
type DraftField = SampleTypeFieldInput & { readonly draftKey: string; readonly fieldType: SampleFieldTypeName };

function SampleTypeEditor({
  type,
  onClose,
  onCreated,
}: {
  /** Null while creating. */
  readonly type: SampleTypeDefinition | null;
  readonly onClose: () => void;
  readonly onCreated?: (created: SampleTypeDefinition) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const locale = currentLocale();
  const [nameAr, setNameAr] = useState(type?.nameAr ?? '');
  const [nameEn, setNameEn] = useState(type?.nameEn ?? '');
  const [code, setCode] = useState('');
  const [drafts, setDrafts] = useState<DraftField[]>([]);
  const [fieldDialog, setFieldDialog] = useState<{ field: SampleTypeField | DraftField | null } | null>(null);
  const [confirmField, setConfirmField] = useState<{ field: SampleTypeField; action: 'archive' | 'delete' } | null>(null);
  const [preview, setPreview] = useState<Record<string, string>>({});

  const refresh = () => queryClient.invalidateQueries({ queryKey: SAMPLE_TYPES_QUERY_KEY });

  const saveType = useMutation({
    mutationFn: async () => {
      if (type) {
        return sampleTypesApi.update(type.id, { nameAr: nameAr.trim() || null, nameEn: nameEn.trim() || null });
      }
      return sampleTypesApi.create({
        ...(code.trim() ? { code: code.trim() } : {}),
        nameAr: nameAr.trim() || null,
        nameEn: nameEn.trim() || null,
        fields: drafts.map(({ draftKey: _key, ...field }) => field),
      });
    },
    onSuccess: async (saved) => {
      show({ tone: 'success', message: t(type ? 'sampleTypes.saved' : 'sampleTypes.created', { name: typeLabel(saved, locale) }) });
      if (type) {
        await refresh();
        onClose();
      } else {
        await onCreated?.(saved);
      }
    },
  });
  const fieldAction = useMutation({
    mutationFn: async (input: { field: SampleTypeField; action: 'archive' | 'restore' | 'delete' | 'up' | 'down' }) => {
      if (!type) return input;
      if (input.action === 'archive') await sampleTypesApi.archiveField(type.id, input.field.id);
      else if (input.action === 'restore') await sampleTypesApi.restoreField(type.id, input.field.id);
      else if (input.action === 'delete') await sampleTypesApi.removeField(type.id, input.field.id);
      else {
        const ids = type.fields.map((field) => field.id);
        const at = ids.indexOf(input.field.id);
        const to = input.action === 'up' ? at - 1 : at + 1;
        if (at < 0 || to < 0 || to >= ids.length) return input;
        [ids[at], ids[to]] = [ids[to] as string, ids[at] as string];
        await sampleTypesApi.reorderFields(type.id, ids);
      }
      return input;
    },
    onSuccess: async (input) => {
      await refresh();
      setConfirmField(null);
      if (input.action !== 'up' && input.action !== 'down') {
        show({ tone: 'success', message: t(`sampleTypes.field_${input.action}d`, { name: fieldLabel(input.field, locale) }) });
      }
    },
  });

  const errors = saveType.isPending ? {} : refusalFields(saveType.error, t);
  const savedFields = type?.fields ?? [];
  const previewFields = type ? activeFields(type) : drafts.map((draft) => draftAsField(draft));
  const nameMissing = nameAr.trim() === '' && nameEn.trim() === '';
  const suggestedCode = suggestStableKey(nameEn) || suggestStableKey(nameAr);

  const moveDraft = (index: number, by: number) =>
    setDrafts((current) => {
      const next = [...current];
      const [item] = next.splice(index, 1);
      if (item) next.splice(index + by, 0, item);
      return next;
    });

  return (
    <Dialog
      isOpen
      onClose={onClose}
      size="xl"
      title={type ? t('sampleTypes.editTitle', { name: typeLabel(type, locale) }) : t('sampleTypes.createTitle')}
      footer={
        <ActionBar>
          <ActionButton priority="tertiary" onPress={onClose} isDisabled={saveType.isPending}>
            {type ? t('common.close') : t('common.cancel')}
          </ActionButton>
          <ActionButton
            priority="primary"
            isLoading={saveType.isPending}
            isDisabled={nameMissing}
            onPress={() => saveType.mutate()}
          >
            {type ? t('sampleTypes.saveNames') : t('sampleTypes.create')}
          </ActionButton>
        </ActionBar>
      }
    >
      <div className="flex min-w-0 flex-col gap-gutter" data-testid="sample-type-editor">
        {saveType.error && Object.keys(errors).length === 0 ? <ErrorNotice error={saveType.error} /> : null}

        <section aria-labelledby="sample-type-basics">
          <h3 id="sample-type-basics" className="mb-stack text-body font-semibold text-default-900">
            {t('sampleTypes.basics')}
          </h3>
          <div className="grid grid-cols-1 gap-gutter sm:grid-cols-2">
            <TextField
              label={t('sampleTypes.nameAr')}
              value={nameAr}
              onChange={setNameAr}
              textDirection="rtl"
              {...(errors.nameAr ? { error: errors.nameAr } : {})}
            />
            <TextField
              label={t('sampleTypes.nameEn')}
              value={nameEn}
              onChange={setNameEn}
              textDirection="ltr"
              description={nameMissing ? t('sampleTypes.nameHint') : undefined}
              {...(errors.nameEn ? { error: errors.nameEn } : {})}
            />
            {type ? (
              <div className="min-w-0">
                <p className="text-caption text-default-600">{t('sampleTypes.code')}</p>
                <p className="mt-1 font-mono text-body text-default-900" dir="ltr">{type.code}</p>
                <p className="mt-0.5 text-caption text-default-600">{t('sampleTypes.codeFixed')}</p>
              </div>
            ) : (
              <TextField
                label={t('sampleTypes.code')}
                value={code}
                onChange={(next) => setCode(next.toLowerCase())}
                textDirection="ltr"
                placeholder={suggestedCode || undefined}
                description={t('sampleTypes.codeHint')}
                {...(errors.code ? { error: errors.code } : {})}
              />
            )}
          </div>
        </section>

        {/* The core facts of every sample: a notice, never editable rows (D296). */}
        <section
          aria-labelledby="sample-type-standard"
          className="rounded-medium border border-default-200 bg-default-50 p-3"
          data-testid="standard-fields-notice"
        >
          <h3 id="sample-type-standard" className="text-body font-semibold text-default-900">
            {t('sampleTypes.standardTitle')}
          </h3>
          <p className="mt-0.5 text-caption text-default-700">{t('sampleTypes.standardBody')}</p>
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {STANDARD_FIELDS.map((key) => (
              <li key={key} className="rounded-small bg-content1 px-2 py-0.5 text-caption text-default-800">
                {t(key)}
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="sample-type-additional">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 id="sample-type-additional" className="text-body font-semibold text-default-900">
                {t('sampleTypes.additionalFields')}
              </h3>
              <p className="text-caption text-default-600">
                {type ? t('sampleTypes.savedAsYouGo') : t('sampleTypes.additionalHint')}
              </p>
            </div>
            <ActionButton
              priority="secondary"
              size="sm"
              isDisabled={(type ? savedFields.length : drafts.length) >= 50}
              onPress={() => setFieldDialog({ field: null })}
            >
              {t('sampleTypes.addField')}
            </ActionButton>
          </div>
          {errors.fields || Object.keys(errors).some((key) => key.startsWith('fields.')) ? (
            <div className="mt-stack">
              <Alert tone="danger" title={t('sampleTypes.fieldsProblem')} />
            </div>
          ) : null}

          {(type ? savedFields.length : drafts.length) === 0 ? (
            <p className="mt-stack text-body text-default-600">{t('sampleTypes.noFields')}</p>
          ) : (
            <ol className="mt-stack flex flex-col divide-y divide-default-200 rounded-medium border border-default-200">
              {(type ? savedFields : drafts).map((field, index, all) => {
                const saved = 'id' in field && typeof field.id === 'string' ? (field as SampleTypeField) : null;
                const label = pickLabel(field.labelAr, field.labelEn, locale) || (saved?.key ?? '');
                return (
                  <li
                    key={saved?.id ?? (field as DraftField).draftKey}
                    className="flex min-w-0 flex-wrap items-center justify-between gap-x-gutter gap-y-1 px-3 py-2"
                    data-testid="sample-type-field-row"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 break-words text-body text-default-900">
                        <span>{label}</span>
                        {saved?.archived ? <StatusBadge label={t('sampleTypes.archived')} /> : null}
                      </div>
                      <p className="text-caption text-default-600">
                        {fieldTypeLabel(t, field.fieldType)}
                        {' · '}
                        {field.isRequired ? t('common.required') : t('common.optional')}
                        {saved ? ` · ${t('sampleTypes.usage', { count: saved.usageCount })}` : ''}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-1">
                      <ActionButton
                        priority="tertiary"
                        size="sm"
                        isDisabled={index === 0 || fieldAction.isPending}
                        aria-label={t('sampleTypes.moveUp', { name: label })}
                        onPress={() => (saved ? fieldAction.mutate({ field: saved, action: 'up' }) : moveDraft(index, -1))}
                      >
                        ↑
                      </ActionButton>
                      <ActionButton
                        priority="tertiary"
                        size="sm"
                        isDisabled={index === all.length - 1 || fieldAction.isPending}
                        aria-label={t('sampleTypes.moveDown', { name: label })}
                        onPress={() => (saved ? fieldAction.mutate({ field: saved, action: 'down' }) : moveDraft(index, 1))}
                      >
                        ↓
                      </ActionButton>
                      <ActionButton
                        priority="secondary"
                        size="sm"
                        aria-label={t('sampleTypes.editFieldLabel', { name: label })}
                        onPress={() => setFieldDialog({ field })}
                      >
                        {t('common.edit')}
                      </ActionButton>
                      {saved ? (
                        saved.archived ? (
                          <ActionButton priority="tertiary" size="sm" onPress={() => fieldAction.mutate({ field: saved, action: 'restore' })}>
                            {t('sampleTypes.restore')}
                          </ActionButton>
                        ) : (
                          <ActionButton priority="tertiary" size="sm" onPress={() => setConfirmField({ field: saved, action: 'archive' })}>
                            {t('sampleTypes.archive')}
                          </ActionButton>
                        )
                      ) : null}
                      {!saved || saved.usageCount === 0 ? (
                        <ActionButton
                          priority="destructive"
                          size="sm"
                          aria-label={t('sampleTypes.removeFieldLabel', { name: label })}
                          onPress={() =>
                            saved
                              ? setConfirmField({ field: saved, action: 'delete' })
                              : setDrafts((current) => current.filter((_, at) => at !== index))
                          }
                        >
                          {t('common.remove')}
                        </ActionButton>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
          {fieldAction.error ? (
            <div className="mt-stack">
              <ErrorNotice error={fieldAction.error} />
            </div>
          ) : null}
        </section>

        {previewFields.length > 0 ? (
          <section
            aria-labelledby="sample-type-preview"
            className="rounded-medium border border-dashed border-default-300 p-3"
            data-testid="sample-type-preview"
          >
            <h3 id="sample-type-preview" className="text-body font-semibold text-default-900">
              {t('sampleTypes.previewTitle')}
            </h3>
            <p className="mb-stack text-caption text-default-600">{t('sampleTypes.previewHint')}</p>
            <AdditionalFieldsInputs
              fields={previewFields}
              values={preview}
              onChange={(fieldId, value) => setPreview((current) => ({ ...current, [fieldId]: value }))}
            />
          </section>
        ) : null}
      </div>

      {fieldDialog ? (
        <FieldDialog
          typeId={type?.id ?? null}
          field={fieldDialog.field}
          siblings={type ? savedFields : drafts}
          onClose={() => setFieldDialog(null)}
          onDraft={(draft) => {
            setDrafts((current) => {
              const at = current.findIndex((entry) => entry.draftKey === draft.draftKey);
              return at >= 0 ? current.map((entry, index) => (index === at ? draft : entry)) : [...current, draft];
            });
            setFieldDialog(null);
          }}
          onSaved={async () => {
            await refresh();
            setFieldDialog(null);
          }}
        />
      ) : null}

      <ConfirmDialog
        isOpen={confirmField !== null}
        onClose={() => {
          setConfirmField(null);
          fieldAction.reset();
        }}
        title={
          confirmField
            ? t(confirmField.action === 'delete' ? 'sampleTypes.deleteFieldTitle' : 'sampleTypes.archiveFieldTitle', {
                name: fieldLabel(confirmField.field, locale),
              })
            : ''
        }
        message={
          confirmField
            ? confirmField.action === 'delete'
              ? t('sampleTypes.deleteFieldBody')
              : t('sampleTypes.archiveFieldBody', { count: confirmField.field.usageCount })
            : ''
        }
        confirmLabel={confirmField?.action === 'delete' ? t('common.remove') : t('sampleTypes.archive')}
        confirmPriority={confirmField?.action === 'delete' ? 'destructive' : 'primary'}
        isPending={fieldAction.isPending}
        onConfirm={() => {
          if (confirmField) fieldAction.mutate(confirmField);
        }}
      >
        {fieldAction.error ? <ErrorNotice error={fieldAction.error} /> : null}
      </ConfirmDialog>
    </Dialog>
  );
}

/** A draft field shaped as a saved one, for the preview. */
function draftAsField(draft: DraftField): SampleTypeField {
  return {
    id: draft.draftKey,
    key: draft.key ?? draft.draftKey,
    fieldType: draft.fieldType,
    labelAr: draft.labelAr ?? null,
    labelEn: draft.labelEn ?? null,
    placeholderAr: draft.placeholderAr ?? null,
    placeholderEn: draft.placeholderEn ?? null,
    helpAr: draft.helpAr ?? null,
    helpEn: draft.helpEn ?? null,
    isRequired: draft.isRequired ?? false,
    sortOrder: 0,
    archived: false,
    usageCount: 0,
    options: (draft.options ?? []).map((option, index) => ({
      id: option.id ?? `${draft.draftKey}-${index}`,
      value: option.value ?? `option_${index + 1}`,
      labelAr: option.labelAr ?? null,
      labelEn: option.labelEn ?? null,
      sortOrder: index,
      archived: option.archived ?? false,
      usageCount: 0,
    })),
  };
}

// ---------------------------------------------------------------------------
// Add / edit one field
// ---------------------------------------------------------------------------

interface OptionDraft {
  readonly rowKey: string;
  readonly id?: string;
  readonly value?: string;
  readonly labelAr: string;
  readonly labelEn: string;
  readonly archived: boolean;
  readonly usageCount: number;
}

let optionCounter = 0;
const newOptionKey = () => `option-${(optionCounter += 1)}`;

function FieldDialog({
  typeId,
  field,
  siblings,
  onClose,
  onDraft,
  onSaved,
}: {
  /** Null while the type itself is still being created: the field is a draft. */
  readonly typeId: string | null;
  readonly field: SampleTypeField | DraftField | null;
  readonly siblings: readonly (SampleTypeField | DraftField)[];
  readonly onClose: () => void;
  readonly onDraft: (draft: DraftField) => void;
  readonly onSaved: () => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const locale = currentLocale();
  const saved = field && 'id' in field && typeof field.id === 'string' ? (field as SampleTypeField) : null;
  const [fieldType, setFieldType] = useState<SampleFieldTypeName>(field?.fieldType ?? 'text');
  const [labelAr, setLabelAr] = useState(field?.labelAr ?? '');
  const [labelEn, setLabelEn] = useState(field?.labelEn ?? '');
  const [placeholderAr, setPlaceholderAr] = useState(field?.placeholderAr ?? '');
  const [placeholderEn, setPlaceholderEn] = useState(field?.placeholderEn ?? '');
  const [helpAr, setHelpAr] = useState(field?.helpAr ?? '');
  const [helpEn, setHelpEn] = useState(field?.helpEn ?? '');
  const [isRequired, setIsRequired] = useState(field?.isRequired ?? false);
  const [options, setOptions] = useState<OptionDraft[]>(() =>
    (field?.options ?? []).map((option) => ({
      rowKey: newOptionKey(),
      ...('id' in option && option.id ? { id: option.id } : {}),
      ...(option.value ? { value: option.value } : {}),
      labelAr: option.labelAr ?? '',
      labelEn: option.labelEn ?? '',
      archived: option.archived ?? false,
      usageCount: 'usageCount' in option ? (option.usageCount as number) : 0,
    })),
  );
  const [localError, setLocalError] = useState<string | null>(null);

  const typeLocked = (saved?.usageCount ?? 0) > 0;
  const becameRequired = saved !== null && !saved.isRequired && isRequired;

  const body = (): SampleTypeFieldInput => ({
    fieldType,
    labelAr: labelAr.trim() || null,
    labelEn: labelEn.trim() || null,
    placeholderAr: placeholderAr.trim() || null,
    placeholderEn: placeholderEn.trim() || null,
    helpAr: helpAr.trim() || null,
    helpEn: helpEn.trim() || null,
    isRequired,
    ...(fieldType === 'select'
      ? {
          options: options.map((option) => ({
            ...(option.id ? { id: option.id } : {}),
            ...(option.value && !option.id ? { value: option.value } : {}),
            labelAr: option.labelAr.trim() || null,
            labelEn: option.labelEn.trim() || null,
            archived: option.archived,
          })),
        }
      : saved && saved.fieldType === 'select'
        ? { options: [] }
        : {}),
  });

  const save = useMutation({
    mutationFn: async () => {
      if (!typeId) return;
      if (saved) await sampleTypesApi.updateField(typeId, saved.id, body());
      else await sampleTypesApi.addField(typeId, body());
    },
    onSuccess: async () => {
      show({ tone: 'success', message: t('sampleTypes.fieldSaved', { name: pickLabel(labelAr, labelEn, locale) }) });
      await onSaved();
    },
  });
  const errors = save.isPending ? {} : refusalFields(save.error, t);

  const submit = () => {
    if (!labelAr.trim() && !labelEn.trim()) return setLocalError(t('sampleTypes.labelRequired'));
    if (fieldType === 'select' && options.filter((option) => !option.archived && (option.labelAr.trim() || option.labelEn.trim())).length === 0) {
      return setLocalError(t('sampleTypes.optionsRequired'));
    }
    if (fieldType === 'select' && options.some((option) => !option.labelAr.trim() && !option.labelEn.trim())) {
      return setLocalError(t('sampleTypes.optionLabelRequired'));
    }
    const taken = siblings.some(
      (other) =>
        other !== field &&
        ((labelEn.trim() && (other.labelEn ?? '').trim().toLowerCase() === labelEn.trim().toLowerCase()) ||
          (labelAr.trim() && (other.labelAr ?? '').trim() === labelAr.trim())),
    );
    if (taken) return setLocalError(t('sampleTypes.configError.label_taken'));
    setLocalError(null);
    if (typeId) return save.mutate();
    onDraft({
      ...body(),
      fieldType,
      draftKey: field && 'draftKey' in field ? field.draftKey : `draft-${newOptionKey()}`,
    });
  };

  const setOption = (rowKey: string, change: Partial<OptionDraft>) =>
    setOptions((current) => current.map((option) => (option.rowKey === rowKey ? { ...option, ...change } : option)));
  const moveOption = (index: number, by: number) =>
    setOptions((current) => {
      const next = [...current];
      const [item] = next.splice(index, 1);
      if (item) next.splice(index + by, 0, item);
      return next;
    });

  return (
    <Dialog
      isOpen
      onClose={onClose}
      size="lg"
      title={field ? t('sampleTypes.editFieldTitle', { name: pickLabel(field.labelAr, field.labelEn, locale) }) : t('sampleTypes.addFieldTitle')}
      footer={
        <ActionBar>
          <ActionButton priority="tertiary" onPress={onClose} isDisabled={save.isPending}>
            {t('common.cancel')}
          </ActionButton>
          <ActionButton priority="primary" isLoading={save.isPending} onPress={submit}>
            {typeId ? t('common.save') : t('sampleTypes.keepField')}
          </ActionButton>
        </ActionBar>
      }
    >
      <div className="flex min-w-0 flex-col gap-gutter" data-testid="field-editor">
        {localError ? <Alert tone="danger" title={localError} /> : null}
        {save.error && Object.keys(errors).length === 0 ? <ErrorNotice error={save.error} /> : null}
        <div className="grid grid-cols-1 gap-gutter sm:grid-cols-2">
          <TextField label={t('sampleTypes.labelAr')} value={labelAr} onChange={setLabelAr} textDirection="rtl" {...(errors.labelAr ? { error: errors.labelAr } : {})} />
          <TextField label={t('sampleTypes.labelEn')} value={labelEn} onChange={setLabelEn} textDirection="ltr" {...(errors.labelEn ? { error: errors.labelEn } : {})} />
          <SelectField
            label={t('sampleTypes.fieldTypeLabel')}
            value={fieldType}
            onChange={(next) => next && setFieldType(next as SampleFieldTypeName)}
            isDisabled={typeLocked}
            isRequired
            description={typeLocked ? t('sampleTypes.typeLocked') : undefined}
            options={ALL_SAMPLE_FIELD_TYPES.map((value) => ({ value, label: fieldTypeLabel(t, value) }))}
            {...(errors.fieldType ? { error: errors.fieldType } : {})}
          />
          <div className="flex items-end pb-1">
            <SwitchField
              label={t('sampleTypes.requiredSwitch')}
              description={t('sampleTypes.requiredHint')}
              value={isRequired}
              onChange={setIsRequired}
            />
          </div>
        </div>
        {becameRequired ? (
          <Alert tone="warning" title={t('sampleTypes.becameRequiredTitle')} description={t('sampleTypes.becameRequiredBody')} />
        ) : null}

        {fieldType === 'select' ? (
          <section aria-labelledby="field-options" data-testid="field-options">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 id="field-options" className="text-body font-semibold text-default-900">
                {t('sampleTypes.options')}
              </h4>
              <ActionButton
                priority="secondary"
                size="sm"
                isDisabled={options.length >= 100}
                onPress={() =>
                  setOptions((current) => [
                    ...current,
                    { rowKey: newOptionKey(), labelAr: '', labelEn: '', archived: false, usageCount: 0 },
                  ])
                }
              >
                {t('sampleTypes.addOption')}
              </ActionButton>
            </div>
            <p className="text-caption text-default-600">{t('sampleTypes.optionsHint')}</p>
            {errors.options ? <p className="mt-1 text-caption text-danger-700">{errors.options}</p> : null}
            <ol className="mt-stack flex flex-col gap-stack">
              {options.map((option, index) => {
                const name = pickLabel(option.labelAr, option.labelEn, locale) || t('sampleTypes.optionN', { n: index + 1 });
                const optionError = Object.entries(errors).find(
                  ([key]) => key === `options.${index}.value` || (option.value && key === `options.${option.value}`),
                )?.[1];
                return (
                  <li
                    key={option.rowKey}
                    className="grid min-w-0 grid-cols-1 items-end gap-x-gutter gap-y-2 rounded-medium border border-default-200 p-2 sm:grid-cols-[1fr_1fr_auto]"
                    data-testid="field-option-row"
                  >
                    <TextField label={t('sampleTypes.labelAr')} value={option.labelAr} onChange={(labelAr) => setOption(option.rowKey, { labelAr })} textDirection="rtl" isDisabled={option.archived} />
                    <TextField
                      label={t('sampleTypes.labelEn')}
                      value={option.labelEn}
                      onChange={(labelEn) => setOption(option.rowKey, { labelEn })}
                      textDirection="ltr"
                      isDisabled={option.archived}
                      {...(optionError ? { error: optionError } : {})}
                    />
                    <div className="flex flex-wrap items-center gap-1">
                      <ActionButton priority="tertiary" size="sm" isDisabled={index === 0} aria-label={t('sampleTypes.moveUp', { name })} onPress={() => moveOption(index, -1)}>
                        ↑
                      </ActionButton>
                      <ActionButton
                        priority="tertiary"
                        size="sm"
                        isDisabled={index === options.length - 1}
                        aria-label={t('sampleTypes.moveDown', { name })}
                        onPress={() => moveOption(index, 1)}
                      >
                        ↓
                      </ActionButton>
                      {option.id ? (
                        <ActionButton priority="tertiary" size="sm" onPress={() => setOption(option.rowKey, { archived: !option.archived })}>
                          {option.archived ? t('sampleTypes.restore') : t('sampleTypes.archive')}
                        </ActionButton>
                      ) : null}
                      {option.usageCount === 0 ? (
                        <ActionButton
                          priority="destructive"
                          size="sm"
                          aria-label={t('sampleTypes.removeOptionLabel', { name })}
                          onPress={() => setOptions((current) => current.filter((entry) => entry.rowKey !== option.rowKey))}
                        >
                          {t('common.remove')}
                        </ActionButton>
                      ) : (
                        <span className="text-caption text-default-600">{t('sampleTypes.usage', { count: option.usageCount })}</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>
        ) : null}

        <details className="rounded-medium border border-default-200 p-3">
          <summary className="cursor-pointer text-body font-medium text-default-900">{t('sampleTypes.presentation')}</summary>
          <div className="mt-stack grid grid-cols-1 gap-gutter sm:grid-cols-2">
            <TextField label={t('sampleTypes.placeholderAr')} value={placeholderAr} onChange={setPlaceholderAr} textDirection="rtl" />
            <TextField label={t('sampleTypes.placeholderEn')} value={placeholderEn} onChange={setPlaceholderEn} textDirection="ltr" />
            <TextField label={t('sampleTypes.helpAr')} value={helpAr} onChange={setHelpAr} textDirection="rtl" />
            <TextField label={t('sampleTypes.helpEn')} value={helpEn} onChange={setHelpEn} textDirection="ltr" />
          </div>
        </details>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Receiving rules: required standard fields and retention (D70, D71)
// ---------------------------------------------------------------------------

/**
 * Which STANDARD fields this laboratory requires for a type, and how long it
 * keeps it. Two endpoints, because they are two permissions. Nothing is
 * configured for any laboratory by the product (D70); retention produces a
 * prompt, never an action (D71).
 */
function ReceivingRulesDialog({ type, onClose }: { readonly type: SampleTypeDefinition; readonly onClose: () => void }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const locale = currentLocale();
  const settings = useQuery({ queryKey: ['sample-type-settings'], queryFn: () => sampleTypeSettingsApi.list() });
  const rule = settings.data?.find((entry) => entry.sampleType === type.code);
  const [fields, setFields] = useState<string[]>(() => [...(rule?.requiredFields ?? [])]);
  const [retentionDays, setRetentionDays] = useState<number | null>(rule?.retentionDays ?? null);

  const save = useMutation({
    mutationFn: async () => {
      if (can(PERMISSIONS.SAMPLES_UPDATE)) {
        await sampleTypeSettingsApi.setRequiredFields({ sampleType: type.code, requiredFields: fields });
      }
      if (can(PERMISSIONS.SAMPLE_STORAGE_MANAGE)) {
        await sampleTypeSettingsApi.setRetention({ sampleType: type.code, retentionDays });
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sample-type-settings'] });
      show({ tone: 'success', message: t('sampleTypeSettings.saved') });
      onClose();
    },
  });

  return (
    <Dialog
      isOpen
      onClose={onClose}
      size="lg"
      title={t('sampleTypeSettings.editFor', { sampleType: typeLabel(type, locale) })}
      footer={
        <ActionBar>
          <ActionButton priority="tertiary" onPress={onClose} isDisabled={save.isPending}>
            {t('common.cancel')}
          </ActionButton>
          <ActionButton priority="primary" isLoading={save.isPending} onPress={() => save.mutate()}>
            {t('common.save')}
          </ActionButton>
        </ActionBar>
      }
    >
      <div className="flex flex-col gap-gutter">
        <CheckboxGroupField
          label={t('sampleTypeSettings.requiredFields')}
          description={t('sampleTypeSettings.requiredFieldsHint')}
          value={fields}
          onChange={setFields}
          isDisabled={!can(PERMISSIONS.SAMPLES_UPDATE)}
          options={ALL_CONFIGURABLE_RECEIVING_FIELDS.map((field) => ({ value: field, label: t(`receivingField.${field}`) }))}
        />
        {/* Empty means "keep it", which is a different statement from zero. */}
        <NumberField
          label={t('sampleTypeSettings.retention')}
          description={t('sampleTypeSettings.retentionHint')}
          value={retentionDays}
          onChange={setRetentionDays}
          isDisabled={!can(PERMISSIONS.SAMPLE_STORAGE_MANAGE)}
          step="1"
          unit={t('sampleTypeSettings.days')}
        />
        <Alert
          tone="warning"
          title={t('sampleTypeSettings.retentionNoticeTitle')}
          description={t('sampleTypeSettings.retentionNoticeBody')}
        />
        {save.error ? <Alert tone="danger" title={t('sampleTypeSettings.saveFailed')} /> : null}
      </div>
    </Dialog>
  );
}
